import type { CollectionDetails, CollectionStats, NftHistory, OwnedNft, SeriesPoint, User } from "./api/schemas";
import { currencyToRub, rubToCurrency } from "./currency";
import { formatMoney } from "./formatters";
import { t } from "./i18n";
import { getUserLevelProgress } from "./levels";
import { priceObservationsToPoints } from "./chart-series";

function msLeft(expires: string | null | undefined, now: number) {
  if (!expires) return null;
  const end = new Date(expires).getTime();
  return Number.isNaN(end) ? null : end - now;
}

/** Remaining time in the selected language ("11ч 24м" / "3d 4h"); "expired" once the deadline has passed. */
export function timeLeft(expires: string | null | undefined, now = Date.now()) {
  const ms = msLeft(expires, now);
  if (ms === null) return null;
  if (ms <= 0) return t("market.time_expired");
  const minutes = Math.floor(ms / 60_000);
  const days = Math.floor(minutes / 1440);
  const hours = Math.floor((minutes % 1440) / 60);
  const mins = minutes % 60;
  if (days > 0) return t("market.time_days_hours", { days, hours });
  if (hours > 0) return t("market.time_hours_minutes", { hours, minutes: mins });
  return mins > 0 ? t("market.time_minutes", { minutes: mins }) : t("market.time_under_minute");
}

export type PortfolioPerformer = { name: string; pnl: number };
export type PortfolioSnapshot = { value: number; count: number; listed: number; best: PortfolioPerformer | undefined; worst: PortfolioPerformer | undefined };

function currentValue(item: OwnedNft) {
  return item.status && item.sale_price > 0 ? item.sale_price : item.pic.price;
}

/**
 * Portfolio from the owned-NFT list alone. `my_nfts` carries `buy_price`, so the best/worst holdings can be derived.
 */
export function derivePortfolio(owned: OwnedNft[]): PortfolioSnapshot {
  const withCost = owned
    .filter((item) => typeof item.buy_price === "number" && item.buy_price > 0)
    .map((item) => ({ name: `${item.pic.collection.name} #${String(item.pic.number)}`, pnl: currentValue(item) - (item.buy_price ?? 0) }));
  const sorted = [...withCost].sort((a, b) => b.pnl - a.pnl);
  return {
    value: owned.reduce((sum, item) => sum + currentValue(item), 0),
    count: owned.length,
    listed: owned.filter((item) => item.status).length,
    best: sorted[0],
    worst: sorted.length > 1 ? sorted[sorted.length - 1] : undefined
  };
}

/**
 * Realized P&L: the sum of all sales minus the sum of all purchases. A purchase price comes from the history row
 * itself or, for NFTs still held, from `my_nfts`; when any row has none the balance would be wrong, so it is unknown.
 */
export function deriveRealizedPnl(history: NftHistory[], owned: OwnedNft[]): number | undefined {
  if (history.length === 0) return undefined;
  const heldCost = new Map(owned.flatMap((item) => typeof item.buy_price === "number" && item.buy_price > 0 ? [[String(item.id), item.buy_price] as const] : []));
  let sales = 0;
  let purchases = 0;
  for (const row of history) {
    const cost = row.buy_price ?? heldCost.get(String(row.id ?? ""));
    if (cost === undefined) return undefined;
    purchases += cost;
    if (row.sale_date) sales += row.sale_price;
  }
  return sales - purchases;
}

export type CollectionStatsView = {
  floor: number | undefined; ceiling: number | undefined; supply: number | undefined; volume: number | undefined; owners: number | undefined;
  listedPercent: number | undefined; description: string | undefined;
};

/** Backend stats win; the collection payload fills what it can (floor, ceiling, supply). */
export function mergeCollectionStats(details: CollectionDetails, stats: CollectionStats | null): CollectionStatsView {
  const listedPercent = stats?.listed_percent ?? (stats?.listed !== undefined && stats.supply ? (stats.listed / stats.supply) * 100 : undefined);
  return {
    floor: stats?.floor_price ?? (details.min_price > 0 ? details.min_price : undefined),
    ceiling: details.max_price > 0 ? details.max_price : undefined,
    supply: stats?.supply ?? (details.nfts.length || undefined),
    volume: stats?.total_volume,
    owners: stats?.owners,
    listedPercent,
    description: stats?.description ?? undefined
  };
}

export function seriesToPoints(series: SeriesPoint[] | undefined) {
  return priceObservationsToPoints(series ?? []);
}

export function getRarityTier(rank: number | undefined, supply: number | undefined) {
  if (!rank || !supply || supply <= 0) return null;
  const share = rank / supply;
  if (share <= 0.01) return { id: "legendary", label: t("market.rarity_legendary") };
  if (share <= 0.05) return { id: "epic", label: t("market.rarity_epic") };
  if (share <= 0.2) return { id: "rare", label: t("market.rarity_rare") };
  if (share <= 0.5) return { id: "uncommon", label: t("market.rarity_uncommon") };
  return { id: "common", label: t("market.rarity_common") };
}

export type Achievement = { id: string; title: string; description: string; current: number; target: number; done: boolean; progress: number };

/**
 * Achievements are derived from data the frontend already has, so they work without a dedicated backend endpoint.
 * Titles/descriptions are in the selected language at call time: call during render, don't memoize across a language switch.
 */
export function evaluateAchievements(input: { user: User; ownedCount: number; hasTwoFactor: boolean; auctionWins?: number }): Achievement[] {
  const { user, ownedCount, hasTwoFactor } = input;
  const turnoverRub = currencyToRub(user.turnover, user.currency);
  const level = getUserLevelProgress(user).level;
  const TRADER_TARGET_RUB = 5_000;
  const WHALE_TARGET_RUB = 100_000;
  const defs: Array<Omit<Achievement, "done" | "progress">> = [
    { id: "first-nft", title: t("market.ach_first_nft_title"), description: t("market.ach_first_nft_desc"), current: ownedCount, target: 1 },
    { id: "collector", title: t("market.ach_collector_title"), description: t("market.ach_collector_desc"), current: ownedCount, target: 10 },
    { id: "curator", title: t("market.ach_curator_title"), description: t("market.ach_curator_desc"), current: ownedCount, target: 50 },
    { id: "trader", title: t("market.ach_trader_title"), description: t("market.ach_turnover_desc", { amount: formatMoney(rubToCurrency(TRADER_TARGET_RUB, user.currency), user.currency) }), current: turnoverRub, target: TRADER_TARGET_RUB },
    { id: "whale", title: t("market.ach_whale_title"), description: t("market.ach_turnover_desc", { amount: formatMoney(rubToCurrency(WHALE_TARGET_RUB, user.currency), user.currency) }), current: turnoverRub, target: WHALE_TARGET_RUB },
    { id: "level-5", title: t("market.ach_level5_title"), description: t("market.ach_level5_desc"), current: level, target: 5 },
    { id: "verified", title: t("market.ach_verified_title"), description: t("market.ach_verified_desc"), current: user.verificated ? 1 : 0, target: 1 },
    { id: "secure", title: t("market.ach_secure_title"), description: t("market.ach_secure_desc"), current: hasTwoFactor ? 1 : 0, target: 1 }
  ];
  if (input.auctionWins !== undefined) {
    for (const target of [1, 3, 5]) {
      defs.push({ id: `auction-wins-${target}`, title: t("market.ach_auction_title", { count: target }), description: t("market.ach_auction_desc", { count: target }), current: input.auctionWins, target });
    }
  }
  return defs.map((item) => ({ ...item, done: item.current >= item.target, progress: Math.min(1, Math.max(0, item.current / item.target)) }));
}

export type AuctionPhase = { id: "upcoming" | "active" | "ended"; label: string };

/** Phase from the server status when present, otherwise from the start/end times. */
export function getAuctionPhase(lot: { status?: "active" | "upcoming" | "ended" | undefined; starts?: string | null | undefined; ends: string }, now = Date.now()): AuctionPhase {
  const remaining = msLeft(lot.ends, now);
  const ended = remaining !== null && remaining <= 0;
  const starts = lot.starts ? new Date(lot.starts).getTime() : NaN;
  const upcoming = !Number.isNaN(starts) && starts > now;
  if (lot.status === "ended" || (!lot.status && ended) || (lot.status === "active" && ended)) return { id: "ended", label: t("market.phase_ended") };
  if (lot.status === "upcoming" || (!lot.status && upcoming)) return { id: "upcoming", label: upcoming ? t("market.phase_starts_in", { time: timeLeft(lot.starts, now) ?? "—" }) : t("market.phase_soon") };
  return { id: "active", label: t("market.phase_left", { time: timeLeft(lot.ends, now) ?? "—" }) };
}
